import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const BUDGETS = ["Under $5,000", "$5,000 – $15,000", "$15,000 – $30,000", "$30,000 – $60,000", "$60,000+", "Not sure yet"];

export default function EnquiryForm({ services = [], defaultService = "" }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", suburb: "", service: defaultService,
    message: "", start_date: "", budget: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name";
    if (!form.phone.trim()) errs.phone = "A phone number helps Lucas reply quickly";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errs.email = "Please enter a valid email";
    if (!form.message.trim()) errs.message = "A few words about the job helps";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      toast.success("Enquiry received. Lucas will be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-lg border border-charcoal/10 bg-paper p-10 text-center"
        data-testid="quote-success-panel"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold/15 text-gold">
          <CheckCircle2 size={26} />
        </span>
        <h3 className="mt-6 font-serif text-3xl text-charcoal">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-charcoal/60">
          Your enquiry is on its way. Lucas personally reads and responds to every request.
        </p>
        <button onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", suburb: "", service: "", message: "", start_date: "", budget: "" }); }}
          className="btn-ghost mt-8" data-testid="quote-again-button">
          Send another enquiry
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="rounded-lg border border-charcoal/10 bg-paper p-6 sm:p-9" data-testid="quote-request-form">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="qf-name">Name *</label>
          <input id="qf-name" className="field" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="quote-name-input" />
          {errors.name && <p className="mt-1.5 text-xs text-red-700">{errors.name}</p>}
        </div>
        <div>
          <label className="field-label" htmlFor="qf-phone">Phone *</label>
          <input id="qf-phone" type="tel" className="field" placeholder="0400 000 000" value={form.phone} onChange={set("phone")} data-testid="quote-phone-input" />
          {errors.phone && <p className="mt-1.5 text-xs text-red-700">{errors.phone}</p>}
        </div>
        <div>
          <label className="field-label" htmlFor="qf-email">Email *</label>
          <input id="qf-email" type="email" className="field" placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="quote-email-input" />
          {errors.email && <p className="mt-1.5 text-xs text-red-700">{errors.email}</p>}
        </div>
        <div>
          <label className="field-label" htmlFor="qf-suburb">Suburb / address</label>
          <input id="qf-suburb" className="field" placeholder="e.g. Olinda" value={form.suburb} onChange={set("suburb")} data-testid="quote-suburb-input" />
        </div>
        <div>
          <label className="field-label" htmlFor="qf-service">What do you have in mind?</label>
          <select id="qf-service" className="field" value={form.service} onChange={set("service")} data-testid="quote-service-select">
            <option value="">Select a service…</option>
            {services.map((s) => (
              <option key={s.id || s} value={s.title || s}>{s.title || s}</option>
            ))}
            <option value="Something else">Something else / not sure yet</option>
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="qf-budget">Budget guide (optional)</label>
          <select id="qf-budget" className="field" value={form.budget} onChange={set("budget")} data-testid="quote-budget-select">
            <option value="">Prefer to discuss</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="field-label" htmlFor="qf-message">Project notes *</label>
          <textarea id="qf-message" rows={5} className="field resize-none" placeholder="Tell us about the space, the ideas you have, and anything that matters: access, timing, materials…" value={form.message} onChange={set("message")} data-testid="quote-message-input" />
          {errors.message && <p className="mt-1.5 text-xs text-red-700">{errors.message}</p>}
        </div>
      </div>

      <button type="submit" disabled={sending} className="btn-gold mt-7 w-full !py-4 disabled:opacity-60 sm:w-auto" data-testid="quote-submit-button">
        {sending ? (
          <><Loader2 size={16} className="animate-spin" /> Sending…</>
        ) : (
          <>Send my enquiry <ArrowRight size={16} /></>
        )}
      </button>
      <p className="mt-4 text-xs leading-relaxed text-charcoal/45">
        Your details go straight to Lucas: no mailing lists, no spam. Enquiries are also recorded in
        the Heritage owner dashboard.
      </p>
    </form>
  );
}
